import type { Employee, LayoutConfig, OrgEdge, OrgState, OrgUnit, Point } from "../types/org";
import { formatUnitHeadcount } from "./headcount";

export const DEFAULT_EDGE_STROKE_WIDTH = 2.5;

export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  unitMinWidth: 120,
  unitMaxWidth: 320,
  unitMinHeight: 56,
  fontSize: 14,
  horizontalGap: 32,
  mGradeGap: 110,
  subtreeGap: 80,
  personNameGap: 6,
  edgeBusGap: 36,
  defaultZoom: 100,
  edgeColor: "#35156f",
  tidyStrength: 0.5,
  showMGradeLines: true,
  forceNoWrap: false,
  showEmployeeNames: true,
  showPGradeSymbols: true,
};

export interface LayoutUnitInput {
  unit: OrgUnit;
  manager?: Employee;
  secretary?: Employee;
  employees: Employee[];
  sameDepartmentMembers?: Employee[];
  sameDepartmentMemberDisplayMode?: SameDepartmentMemberDisplayMode;
  staffToggleAvailable?: boolean;
}

export interface LayoutBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface LayoutGradeRow {
  grade: string;
  y: number;
  index: number;
  level: number;
}

export interface LayoutUnitMetrics {
  width: number;
  height: number;
  groupWidth: number;
  groupHeight: number;
  nameLines: string[];
  managerText: string;
  pGradeSymbols: string[];
  staffCount: number;
  belowStaffCount: number;
  belowStaffRows: number;
  externalStaffCount: number;
  externalStaffWidth: number;
  externalStaffHeight: number;
  externalStaffTop: number;
}

export interface LayoutUnitNode {
  unit: OrgUnit;
  x: number;
  y: number;
  scale: number;
  width: number;
  height: number;
  groupWidth: number;
  groupHeight: number;
  grade: string;
  rowIndex: number;
  metrics: LayoutUnitMetrics;
  manager?: Employee;
  secretary?: Employee;
  employees: Employee[];
  sameDepartmentMembers: Employee[];
  sameDepartmentMemberDisplayMode: SameDepartmentMemberDisplayMode;
  staffToggleAvailable: boolean;
}

export type SameDepartmentMemberDisplayMode = "external-leader-line" | "below-node";

export interface LayoutRoutedEdge {
  edge: OrgEdge;
  source: LayoutUnitNode;
  target: LayoutUnitNode;
  path: string;
}

export interface OrgLayout {
  units: LayoutUnitNode[];
  gradeRows: LayoutGradeRow[];
  edges: LayoutRoutedEdge[];
  dashedEdges: LayoutRoutedEdge[];
  warnings: string[];
  chartBounds: LayoutBounds;
}

interface BuildOrgLayoutOptions {
  units: LayoutUnitInput[];
  edges: OrgEdge[];
  dashedEdges: OrgEdge[];
  language: "zh" | "en";
  layoutConfig?: Partial<LayoutConfig>;
  chartBounds: LayoutBounds;
  preserveManual?: boolean;
  slideScopeId?: string;
}

interface DraftNode {
  input: LayoutUnitInput;
  metrics: LayoutUnitMetrics;
  depth: number;
  rowKey: string;
  subtreeWidth: number;
  x: number;
  y: number;
}

const TEXT_FONT_FAMILY = `"Gill Sans MT", "Microsoft JhengHei", sans-serif`;
const OUTER_PAD_X = 96;
const OUTER_PAD_BOTTOM = 96;
const EXTERNAL_STAFF_GAP = 12;
const EXTERNAL_STAFF_LEADER_WIDTH = 34;
const EXTERNAL_STAFF_ICON_WIDTH = 18;
const EXTERNAL_STAFF_TEXT_GAP = 6;
const BELOW_STAFF_COLUMN_SIZE = 5;
const BELOW_STAFF_ROW_GAP = 2;
const BELOW_STAFF_ROW_HEIGHT = 19;
const NO_WRAP_TEXT_WIDTH_BUFFER = 1.16;
const NO_WRAP_EXTRA_PADDING = 10;

export function normalizeLayoutConfig(config?: Partial<LayoutConfig>): LayoutConfig {
  const next = { ...DEFAULT_LAYOUT_CONFIG, ...(config ?? {}) };
  const unitMinWidth = clampNumber(next.unitMinWidth, 100, 520);
  const unitMaxWidth = Math.max(clampNumber(next.unitMaxWidth, 120, 760), unitMinWidth);
  return {
    unitMinWidth,
    unitMaxWidth,
    unitMinHeight: clampNumber(next.unitMinHeight, 48, 260),
    fontSize: clampNumber(next.fontSize, 12, 28),
    horizontalGap: clampNumber(next.horizontalGap, 24, 220),
    mGradeGap: clampNumber(next.mGradeGap, 80, 260),
    subtreeGap: clampNumber(next.subtreeGap, 40, 280),
    personNameGap: clampNumber(next.personNameGap, 4, 24),
    edgeBusGap: clampNumber(next.edgeBusGap, 24, 90),
    defaultZoom: clampNumber(next.defaultZoom, 80, 100),
    edgeColor: /^#[0-9a-f]{6}$/i.test(next.edgeColor) ? next.edgeColor : DEFAULT_LAYOUT_CONFIG.edgeColor,
    tidyStrength: clampNumber(next.tidyStrength, 0, 1),
    showMGradeLines: Boolean(next.showMGradeLines),
    forceNoWrap: Boolean(next.forceNoWrap),
    showEmployeeNames: Boolean(next.showEmployeeNames),
    showPGradeSymbols: Boolean(next.showPGradeSymbols),
  };
}

export function ensureLayoutConfig(state: OrgState): OrgState {
  state.layoutConfig = normalizeLayoutConfig(state.layoutConfig);
  return state;
}

export function buildOrgLayout(options: BuildOrgLayoutOptions): OrgLayout {
  const config = normalizeLayoutConfig(options.layoutConfig);
  const first = buildReadableTree(options, config);
  const failures = readabilityFailures(first, config);
  if (!failures.length) return first;

  const relaxed = normalizeLayoutConfig({
    ...config,
    horizontalGap: config.horizontalGap + 16,
    subtreeGap: config.subtreeGap + 32,
    mGradeGap: config.mGradeGap + 24,
    edgeBusGap: config.edgeBusGap + 10,
    fontSize: Math.max(14, config.fontSize),
  });
  const second = buildReadableTree(options, relaxed);
  second.warnings = [...second.warnings, ...readabilityFailures(second, relaxed)];
  return second;
}

export async function autoLayout(state: OrgState): Promise<OrgState> {
  const next = structuredClone(state);
  ensureLayoutConfig(next);
  next.changeLog.push({ id: `change-${Date.now()}`, label: "自動排版", detail: "自動排版" });
  return next;
}

function buildReadableTree(options: BuildOrgLayoutOptions, config: LayoutConfig): OrgLayout {
  const bounds = options.chartBounds;
  const slideScopeId = options.slideScopeId ?? "";
  const inputs = options.units.filter((item) => !item.unit.isUnlinked);
  const unitIds = new Set(inputs.map((item) => item.unit.id));
  const children = childrenMapFor(inputs.map((item) => item.unit), unitIds);
  const inputById = new Map(inputs.map((item) => [item.unit.id, item]));
  const metrics = new Map(inputs.map((item) => [item.unit.id, measureUnit(item, options.language, config)]));
  const drafts = new Map<string, DraftNode>();
  const roots = inputs
    .map((item) => item.unit)
    .filter((unit) => !unit.parentId || !unitIds.has(unit.parentId))
    .sort(compareUnit);

  const walk = (unit: OrgUnit, depth: number, parentGrade = "", parentSameGradeDepth = 0): number => {
    const input = inputById.get(unit.id);
    const unitMetrics = metrics.get(unit.id);
    if (!input || !unitMetrics) return 0;
    const grade = normalizeMGrade(unit.mGrade);
    const sameGradeDepth = grade && grade === parentGrade ? parentSameGradeDepth + 1 : 0;
    const childUnits = [...(children.get(unit.id) ?? [])].sort(compareUnit);
    const childWidths = childUnits.map((child) => walk(child, depth + 1, grade, sameGradeDepth));
    const gap = childUnits.length > 1 ? config.horizontalGap : 0;
    const childWidth = sumWithGap(childWidths, gap);
    const subtreeWidth = Math.max(unitMetrics.groupWidth, childWidth);
    drafts.set(unit.id, {
      input,
      metrics: unitMetrics,
      depth,
      rowKey: rowKeyForDraft(unit, depth, sameGradeDepth),
      subtreeWidth,
      x: bounds.left,
      y: bounds.top,
    });
    return subtreeWidth;
  };

  for (const root of roots) walk(root, 0);

  let cursorX = bounds.left;
  for (const root of roots) {
    const subtreeWidth = drafts.get(root.id)?.subtreeWidth ?? metrics.get(root.id)?.groupWidth ?? metrics.get(root.id)?.width ?? 0;
    assignSubtree(root, cursorX, subtreeWidth);
    cursorX += subtreeWidth + config.subtreeGap;
  }

  assignGradeRows([...drafts.values()], config, bounds.top);
  if (options.preserveManual) {
    for (const draft of drafts.values()) draft.x += slideManualOffsetFor(draft.input.unit, slideScopeId)?.x ?? 0;
  }
  avoidNodeCollisions([...drafts.values()], config);
  centerDraftX([...drafts.values()], bounds);
  if (options.preserveManual) applySlidePositions([...drafts.values()], config, slideScopeId);

  const nodes = [...drafts.values()].map((draft) => {
    return {
      unit: draft.input.unit,
      x: round(draft.x),
      y: round(draft.y),
      scale: 1,
      width: draft.metrics.width,
      height: draft.metrics.height,
      groupWidth: draft.metrics.groupWidth,
      groupHeight: draft.metrics.groupHeight,
      grade: draft.rowKey,
      rowIndex: draft.depth,
      metrics: draft.metrics,
      manager: draft.input.manager,
      secretary: draft.input.secretary,
      employees: draft.input.employees,
      sameDepartmentMembers: draft.input.sameDepartmentMembers ?? [],
      sameDepartmentMemberDisplayMode: draft.input.sameDepartmentMemberDisplayMode ?? "below-node",
      staffToggleAvailable: Boolean(draft.input.staffToggleAvailable),
    };
  });

  const extents = extentsFor(nodes, bounds);
  const gradeRows = gradeRowsFromNodes(nodes, extents, config);
  const nodeMap = new Map(nodes.map((node) => [node.unit.id, node]));
  const solidEdges = routeEdges(options.edges, nodeMap, config);
  const dashedEdges = routeEdges(options.dashedEdges, nodeMap, config);

  return {
    units: nodes,
    gradeRows,
    edges: solidEdges,
    dashedEdges,
    warnings: detectWarnings(nodes, [...solidEdges, ...dashedEdges], extents),
    chartBounds: extents,
  };

  function assignSubtree(unit: OrgUnit, left: number, subtreeWidth: number) {
    const draft = drafts.get(unit.id);
    if (!draft) return;
    const childUnits = [...(children.get(unit.id) ?? [])].sort(compareUnit);
    if (!childUnits.length) {
      draft.x = left + (subtreeWidth - draft.metrics.groupWidth) / 2;
      return;
    }

    const childWidths = childUnits.map((child) => drafts.get(child.id)?.subtreeWidth ?? metrics.get(child.id)?.groupWidth ?? metrics.get(child.id)?.width ?? 0);
    const totalChildWidth = sumWithGap(childWidths, config.horizontalGap);
    let childLeft = left + (subtreeWidth - totalChildWidth) / 2;
    for (let index = 0; index < childUnits.length; index += 1) {
      assignSubtree(childUnits[index], childLeft, childWidths[index]);
      childLeft += childWidths[index] + config.horizontalGap;
    }
    const first = drafts.get(childUnits[0].id);
    const last = drafts.get(childUnits[childUnits.length - 1].id);
    const center = first && last
      ? (first.x + first.metrics.width / 2 + last.x + last.metrics.width / 2) / 2
      : left + subtreeWidth / 2;
    draft.x = center - draft.metrics.width / 2;
  }
}

function applySlidePositions(nodes: DraftNode[], config: LayoutConfig, slideScopeId: string) {
  const rowTargets = manualRowTargets(nodes, config, slideScopeId);
  for (const node of nodes) {
    const position = slidePositionFor(node.input.unit, slideScopeId);
    if (position && Number.isFinite(position.x)) node.x = position.x;
    node.y = rowTargets.get(node.rowKey) ?? node.y;
  }
}

function manualRowTargets(nodes: DraftNode[], config: LayoutConfig, slideScopeId: string) {
  const rows = new Map<string, DraftNode[]>();
  for (const node of nodes) rows.set(node.rowKey, [...(rows.get(node.rowKey) ?? []), node]);

  const targets = [...rows.entries()]
    .map(([key, rowNodes]) => {
      const manualYs = rowNodes
        .map((node) => slidePositionFor(node.input.unit, slideScopeId)?.y)
        .filter((value): value is number => Number.isFinite(value));
      return {
        key,
        y: manualYs.length ? medianNumber(manualYs) : Math.min(...rowNodes.map((node) => node.y)),
        height: Math.max(...rowNodes.map((node) => node.metrics.groupHeight), config.unitMinHeight),
      };
    })
    .sort((a, b) => compareRowKey(a.key, b.key));

  for (let index = 1; index < targets.length; index += 1) {
    const previous = targets[index - 1];
    const current = targets[index];
    const minY = previous.y + Math.max(config.mGradeGap, previous.height + config.edgeBusGap + 28);
    if (current.y < minY) current.y = minY;
  }

  return new Map(targets.map((target) => [target.key, target.y]));
}

function slidePositionFor(unit: OrgUnit, slideScopeId: string) {
  const scoped = unit.slidePositionByRootId?.[slideScopeId];
  if (scoped && Number.isFinite(scoped.x) && Number.isFinite(scoped.y)) return scoped;
  // Legacy coordinates were global and cannot safely be reused by a custom root.
  // Keep them only for the full-chart scope while old data is being migrated.
  return slideScopeId === "" && unit.slidePosition && Number.isFinite(unit.slidePosition.x) && Number.isFinite(unit.slidePosition.y)
    ? unit.slidePosition
    : undefined;
}

function slideManualOffsetFor(unit: OrgUnit, slideScopeId: string) {
  const scoped = unit.slideManualOffsetByRootId?.[slideScopeId];
  if (scoped && Number.isFinite(scoped.x) && Number.isFinite(scoped.y)) return scoped;
  return slideScopeId === "" ? unit.slideManualOffset : undefined;
}

function medianNumber(values: number[]) {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function measureUnit(input: LayoutUnitInput, language: "zh" | "en", config: LayoutConfig): LayoutUnitMetrics {
  const unit = input.unit;
  const name = language === "en" ? unit.nameEn || unit.nameZh || unit.code : unit.nameZh || unit.nameEn || unit.code;
  const managerName = language === "en" ? unit.managerNameEn || unit.managerNameZh || "" : unit.managerNameZh || unit.managerNameEn || "";
  const managerText = unit.isConcurrent && managerName ? `${managerName} (兼)` : managerName;
  const font = `${config.fontSize}px ${TEXT_FONT_FAMILY}`;
  const managerFont = `${Math.max(14, config.fontSize)}px ${TEXT_FONT_FAMILY}`;
  const metaWidth = measureTextWidth(`${unit.code || "NO CODE"} ${formatUnitHeadcount(unit)}`, `${Math.max(12, config.fontSize - 1)}px ${TEXT_FONT_FAMILY}`);
  const horizontalPadding = Math.max(14, config.fontSize * 1.1);
  const maxContentWidth = config.unitMaxWidth - horizontalPadding * 2;
  const nameLines = config.forceNoWrap ? [name] : wrapText(name, font, maxContentWidth);
  const measuredNameWidth = measureTextWidth(name, font);
  const nameWidth = config.forceNoWrap
    ? measuredNameWidth * NO_WRAP_TEXT_WIDTH_BUFFER + NO_WRAP_EXTRA_PADDING
    : measuredNameWidth;
  const contentWidth = Math.max(
    metaWidth,
    measureTextWidth(managerText || "TBD", managerFont),
    ...nameLines.map((line) => measureTextWidth(line, font)),
    config.forceNoWrap ? nameWidth : 0,
  );
  const width = config.forceNoWrap
    ? Math.max(config.unitMinWidth, Math.ceil(contentWidth + horizontalPadding * 2))
    : clampNumber(Math.ceil(contentWidth + horizontalPadding * 2), config.unitMinWidth, config.unitMaxWidth);
  const pGradeSymbols = config.showPGradeSymbols && !unit.isConcurrent
    ? [input.manager?.pGrade].filter((grade): grade is string => Boolean(grade?.trim()))
    : [];
  const pGradeRows = pGradeSymbols.length ? Math.ceil(pGradeSymbols.length / Math.max(1, Math.floor((width - 20) / 18))) : 0;
  const pGradeHeight = pGradeRows ? pGradeRows * 16 + 6 : 14;
  const nameHeight = Math.max(1, nameLines.length) * Math.ceil(config.fontSize * 1.18);
  const managerHeight = Math.ceil(Math.max(14, config.fontSize) * 1.18);
  const height = Math.max(config.unitMinHeight, nameHeight + managerHeight + pGradeHeight + 13);
  const staffLineHeight = Math.ceil(Math.max(BELOW_STAFF_ROW_HEIGHT, Math.max(12, config.fontSize - 1) * 1.25));
  const sameDepartmentMembers = input.sameDepartmentMembers ?? [];
  const displayMode = input.sameDepartmentMemberDisplayMode ?? "below-node";
  const sameDepartmentBelowCount = displayMode === "below-node" ? sameDepartmentMembers.length : 0;
  const secretaryBelowCount = displayMode === "below-node" && input.secretary ? 1 : 0;
  const secretaryExternalCount = displayMode === "external-leader-line" && input.secretary ? 1 : 0;
  const belowStaffCount = config.showEmployeeNames ? input.employees.length + sameDepartmentBelowCount + secretaryBelowCount : 0;
  const externalStaffCount = config.showEmployeeNames && displayMode === "external-leader-line" ? sameDepartmentMembers.length + secretaryExternalCount : 0;
  const belowStaffRows = Math.min(BELOW_STAFF_COLUMN_SIZE, belowStaffCount);
  const staffHeight = belowStaffRows
    ? config.personNameGap + belowStaffRows * staffLineHeight + Math.max(0, belowStaffRows - 1) * BELOW_STAFF_ROW_GAP
    : 0;
  const externalStaffMembers = [...sameDepartmentMembers, input.secretary].filter((employee): employee is Employee => Boolean(employee));
  const externalStaffNamesWidth = externalStaffCount
    ? Math.max(...externalStaffMembers.map((employee) => measureTextWidth(displayEmployeeName(employee, language), `${Math.max(11, config.fontSize - 1)}px ${TEXT_FONT_FAMILY}`)))
    : 0;
  const externalStaffWidth = externalStaffCount
    ? EXTERNAL_STAFF_GAP + EXTERNAL_STAFF_LEADER_WIDTH + EXTERNAL_STAFF_ICON_WIDTH + EXTERNAL_STAFF_TEXT_GAP + Math.ceil(externalStaffNamesWidth) + 10
    : 0;
  const externalStaffHeight = externalStaffCount ? config.personNameGap + externalStaffCount * staffLineHeight : 0;
  const externalStaffTop = externalStaffCount
    ? Math.min(Math.max(0, height - externalStaffHeight), height + config.edgeBusGap - 24 - externalStaffHeight)
    : 0;
  const groupWidth = width + externalStaffWidth;
  return {
    width,
    height,
    groupWidth,
    groupHeight: Math.max(height + staffHeight, externalStaffTop + externalStaffHeight, height),
    nameLines,
    managerText,
    pGradeSymbols,
    staffCount: belowStaffCount + externalStaffCount,
    belowStaffCount,
    belowStaffRows,
    externalStaffCount,
    externalStaffWidth,
    externalStaffHeight,
    externalStaffTop,
  };
}

function assignGradeRows(nodes: DraftNode[], config: LayoutConfig, top: number) {
  const byRow = new Map<string, DraftNode[]>();
  for (const node of nodes) {
    const key = node.rowKey;
    byRow.set(key, [...(byRow.get(key) ?? []), node]);
  }
  let y = top;
  for (const key of [...byRow.keys()].sort(compareRowKey)) {
    const row = byRow.get(key) ?? [];
    for (const node of row) node.y = y;
    const rowHeight = Math.max(...row.map((node) => node.metrics.groupHeight), config.unitMinHeight);
    y += Math.max(config.mGradeGap, rowHeight + config.edgeBusGap + 28);
  }
}

function avoidNodeCollisions(nodes: DraftNode[], config: LayoutConfig) {
  const byY = new Map<number, DraftNode[]>();
  for (const node of nodes) byY.set(Math.round(node.y), [...(byY.get(Math.round(node.y)) ?? []), node]);
  for (const row of byY.values()) {
    row.sort((a, b) => a.x - b.x || compareUnit(a.input.unit, b.input.unit));
    let right = -Infinity;
    for (const node of row) {
      const minGap = parentIdOf(node) === parentIdOfPrevious(row, node) ? config.horizontalGap : config.subtreeGap;
      if (node.x < right + minGap) node.x = right + minGap;
      right = node.x + node.metrics.groupWidth;
    }
  }
}

function centerDraftX(nodes: DraftNode[], bounds: LayoutBounds) {
  if (!nodes.length) return;
  const minX = Math.min(...nodes.map((node) => node.x));
  const maxX = Math.max(...nodes.map((node) => node.x + node.metrics.groupWidth));
  const width = maxX - minX;
  const availableWidth = bounds.right - bounds.left;
  const targetLeft = bounds.left + Math.max(0, (availableWidth - width) / 2);
  const dx = targetLeft - minX;
  for (const node of nodes) node.x += dx;
}

function gradeRowsFromNodes(nodes: LayoutUnitNode[], bounds: LayoutBounds, config: LayoutConfig): LayoutGradeRow[] {
  const byGrade = new Map<string, LayoutUnitNode[]>();
  for (const node of nodes) {
    byGrade.set(node.grade, [...(byGrade.get(node.grade) ?? []), node]);
  }
  const rows = [...byGrade.entries()]
    .map(([grade, rowNodes]) => {
      const y = Math.min(...rowNodes.map((node) => node.y));
      return {
        grade,
        y,
        index: Math.min(...rowNodes.map((node) => node.rowIndex)),
        level: levelFromGrade(grade, Math.min(...rowNodes.map((node) => node.rowIndex))),
      };
    })
    .sort((a, b) => a.y - b.y);
  if (rows.length) return rows;
  return ["M8", "M7", "M6", "M5"].map((grade, index) => ({
    grade,
    y: bounds.top + index * config.mGradeGap,
    index,
    level: levelFromGrade(grade, index),
  }));
}

function routeEdges(edges: OrgEdge[], nodeMap: Map<string, LayoutUnitNode>, config: LayoutConfig): LayoutRoutedEdge[] {
  const nodes = [...nodeMap.values()];
  const busBySource = new Map<string, number>();
  return edges
    .map((edge) => {
      const source = nodeMap.get(edge.source);
      const target = nodeMap.get(edge.target);
      if (!source || !target) return undefined;
      const busY = busBySource.get(source.unit.id) ?? source.y + source.height + config.edgeBusGap;
      busBySource.set(source.unit.id, busY);
      return { edge, source, target, path: treeConnectorPath(source, target, busY, nodes, config) };
    })
    .filter((edge): edge is LayoutRoutedEdge => Boolean(edge));
}

function treeConnectorPath(source: LayoutUnitNode, target: LayoutUnitNode, busY: number, nodes: LayoutUnitNode[], config: LayoutConfig) {
  const sx = source.x + source.width / 2;
  const sy = source.y + source.height;
  const tx = target.x + target.width / 2;
  const ty = target.y;
  const maxBusY = Math.max(sy + config.edgeBusGap, ty - config.edgeBusGap / 2);
  const candidates = uniqueNumbers([busY, sy + config.edgeBusGap, Math.min(maxBusY, sy + config.edgeBusGap + 20), ty - config.edgeBusGap / 2]);
  for (const candidate of candidates) {
    const path = [{ x: sx, y: sy }, { x: sx, y: candidate }, { x: tx, y: candidate }, { x: tx, y: ty }];
    if (!pathHitsNodes(path, source.unit.id, target.unit.id, nodes)) return pathFor(path);
  }
  const detourX = target.x > source.x ? target.x - config.horizontalGap / 2 : target.x + target.width + config.horizontalGap / 2;
  return pathFor([{ x: sx, y: sy }, { x: sx, y: busY }, { x: detourX, y: busY }, { x: detourX, y: ty - 14 }, { x: tx, y: ty - 14 }, { x: tx, y: ty }]);
}

function pathHitsNodes(points: Point[], sourceId: string, targetId: string, nodes: LayoutUnitNode[]) {
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    for (const node of nodes) {
      if (node.unit.id === sourceId || node.unit.id === targetId) continue;
      if (segmentIntersectsRect(a, b, rectForNode(node, 4))) return true;
    }
  }
  return false;
}

function detectWarnings(nodes: LayoutUnitNode[], edges: LayoutRoutedEdge[], bounds: LayoutBounds) {
  const warnings: string[] = [];
  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      if (rectsOverlap(rectForNode(nodes[i]), rectForNode(nodes[j]), 4)) {
        warnings.push("Some nodes or staff labels overlap after layout.");
        i = nodes.length;
        break;
      }
    }
  }
  if (edges.some((edge) => pathHitsNodes(pathPoints(edge.path), edge.source.unit.id, edge.target.unit.id, nodes))) {
    warnings.push("Some connector routes cross node or staff-label areas.");
  }
  if (nodes.some((node) => node.metrics.groupHeight > bounds.bottom - bounds.top)) warnings.push("Some staff lists are taller than the current chart area.");
  return unique(warnings);
}

function readabilityFailures(layout: OrgLayout, config: LayoutConfig) {
  const failures = [...layout.warnings];
  if (config.defaultZoom < 80) failures.push("Default zoom is below readable range.");
  if (config.fontSize < 14) failures.push("Node text is below presentation readable size.");
  if ([...layout.edges, ...layout.dashedEdges].some((edge) => longestVerticalSegment(edge.path) > 220)) failures.push("A parent-child connector has a vertical segment longer than 220px.");
  return unique(failures);
}

function longestVerticalSegment(path: string) {
  const points = pathPoints(path);
  let longest = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    if (Math.abs(a.x - b.x) < 0.1) longest = Math.max(longest, Math.abs(a.y - b.y));
  }
  return longest;
}

function extentsFor(nodes: LayoutUnitNode[], fallback: LayoutBounds): LayoutBounds {
  if (!nodes.length) return fallback;
  return {
    left: fallback.left,
    top: fallback.top,
    right: Math.max(fallback.right, Math.ceil(Math.max(...nodes.map((node) => node.x + node.groupWidth)) + OUTER_PAD_X)),
    bottom: Math.max(fallback.bottom, Math.ceil(Math.max(...nodes.map((node) => node.y + node.groupHeight)) + OUTER_PAD_BOTTOM)),
  };
}

function measureTextWidth(text: string, font: string) {
  if (!text) return 0;
  const canvas = getMeasureCanvas();
  if (canvas) {
    const context = canvas.getContext("2d");
    if (context) {
      context.font = font;
      return context.measureText(text).width;
    }
  }
  const size = Number(font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? 16);
  return [...text].reduce((width, char) => width + (/[\u4e00-\u9fff]/.test(char) ? size : size * 0.58), 0);
}

function displayEmployeeName(employee: Employee, language: "zh" | "en") {
  return language === "en"
    ? employee.englishName || employee.chineseName || employee.employeeNo
    : employee.chineseName || employee.englishName || employee.employeeNo;
}

function getMeasureCanvas(): HTMLCanvasElement | OffscreenCanvas | undefined {
  if (typeof document !== "undefined") return document.createElement("canvas");
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(1, 1);
  return undefined;
}

function wrapText(text: string, font: string, maxWidth: number) {
  if (!text) return [""];
  if (measureTextWidth(text, font) <= maxWidth) return [text];
  const tokens = text.includes(" ") ? text.split(/(\s+)/) : [...text];
  const lines: string[] = [];
  let current = "";
  for (const token of tokens) {
    const next = `${current}${token}`;
    if (current && measureTextWidth(next, font) > maxWidth) {
      lines.push(current.trimEnd());
      current = token.trimStart();
    } else {
      current = next;
    }
  }
  if (current) lines.push(current.trimEnd());
  return lines.length ? lines : [text];
}

function childrenMapFor(units: OrgUnit[], visibleIds: Set<string>) {
  const children = new Map<string, OrgUnit[]>();
  for (const unit of units) {
    if (!unit.parentId || !visibleIds.has(unit.parentId)) continue;
    children.set(unit.parentId, [...(children.get(unit.parentId) ?? []), unit]);
  }
  return children;
}

function parentIdOf(node: DraftNode) {
  return node.input.unit.parentId ?? "";
}

function parentIdOfPrevious(row: DraftNode[], node: DraftNode) {
  const index = row.indexOf(node);
  return index > 0 ? parentIdOf(row[index - 1]) : parentIdOf(node);
}

function normalizeMGrade(value?: string) {
  const match = String(value ?? "").match(/M*\s*(\d+)/i);
  return match ? `M${match[1]}` : "";
}

function rowKeyForDraft(unit: OrgUnit, depth: number, sameGradeDepth: number) {
  const grade = normalizeMGrade(unit.mGrade);
  if (!grade) return `L${depth}`;
  return sameGradeDepth ? `${grade}.${sameGradeDepth}` : grade;
}

function compareRowKey(a: string, b: string) {
  const aGrade = mGradeRowParts(a);
  const bGrade = mGradeRowParts(b);
  if (aGrade || bGrade) {
    if (!aGrade) return 1;
    if (!bGrade) return -1;
    return bGrade.grade - aGrade.grade || aGrade.subrow - bGrade.subrow;
  }
  return levelFromGrade(a, 0) - levelFromGrade(b, 0);
}

function mGradeNumber(value?: string) {
  const match = String(value ?? "").match(/^M(\d+)$/i);
  return match ? Number(match[1]) : 0;
}

function compareMGradeDesc(a: string, b: string) {
  return Number(b.slice(1)) - Number(a.slice(1));
}

function levelFromGrade(grade: string, fallback: number) {
  const mGrade = mGradeRowParts(grade);
  if (mGrade) return 12 - mGrade.grade + mGrade.subrow * 0.5;
  if (grade.startsWith("L")) return Number(grade.slice(1));
  return fallback;
}

function mGradeRowParts(value?: string) {
  const match = String(value ?? "").match(/^M(\d+)(?:\.(\d+))?$/i);
  return match ? { grade: Number(match[1]), subrow: Number(match[2] ?? 0) } : undefined;
}

function compareUnit(a: OrgUnit, b: OrgUnit) {
  return a.code.localeCompare(b.code, undefined, { numeric: true }) || a.level - b.level || a.nameZh.localeCompare(b.nameZh);
}

function sumWithGap(values: number[], gap: number) {
  return values.reduce((sum, value) => sum + value, 0) + Math.max(0, values.length - 1) * gap;
}

function rectForNode(node: LayoutUnitNode, inflate = 0) {
  return {
    left: node.x - inflate,
    top: node.y - inflate,
    right: node.x + node.groupWidth + inflate,
    bottom: node.y + node.groupHeight + inflate,
  };
}

function rectsOverlap(a: ReturnType<typeof rectForNode>, b: ReturnType<typeof rectForNode>, gap = 0) {
  return a.left < b.right + gap && a.right + gap > b.left && a.top < b.bottom + gap && a.bottom + gap > b.top;
}

function segmentIntersectsRect(a: Point, b: Point, rect: ReturnType<typeof rectForNode>) {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  return maxX >= rect.left && minX <= rect.right && maxY >= rect.top && minY <= rect.bottom;
}

function pathFor(points: Point[]) {
  return points.map((point, index) => `${index ? "L" : "M"} ${round(point.x)} ${round(point.y)}`).join(" ");
}

function pathPoints(path: string): Point[] {
  const values = path.match(/-?\d+(?:\.\d+)?/g)?.map(Number) ?? [];
  const points: Point[] = [];
  for (let index = 0; index < values.length; index += 2) points.push({ x: values[index], y: values[index + 1] });
  return points;
}

function unique<T>(values: T[]) {
  return [...new Set(values)];
}

function uniqueNumbers(values: number[]) {
  return [...new Set(values.map((value) => Math.round(value)).filter((value) => Number.isFinite(value)))];
}

function round(value: number) {
  return Math.round(value * 10) / 10;
}

function clampNumber(value: number, min: number, max: number) {
  const safe = Number.isFinite(value) ? value : min;
  return Math.min(max, Math.max(min, safe));
}
